import type {
  ArticleService, DraftUploadMetadata, ArticleSearchFilters,
  SaveArticleResult, ListSavedArticlesParams, ListRecentlyReadParams,
} from '../services/articleService'
import type { ListDraftsParams, WireArticle, WireArticleFull, WireArticleListPage } from '../types'
import { publishingApiClient } from './publishingApiClient'
import { articlesApiClient } from './articlesApiClient'

type PatchWorkflowStage = import('../types').WireWorkflowStage | 'blocked'

export type NavMethod = 'gen_link' | 'search_result' | 'email' | 'page' | 'ai' | 'recommended'

// The backend uses `articleId` as the primary record ID; the frontend type uses `id`.
// Normalize at the API boundary so the rest of the app doesn't need to know.
function normalizeDraft(raw: WireArticle & { articleId?: string }): WireArticle {
  const { articleId, ...rest } = raw
  return { ...rest, id: rest.id ?? articleId ?? '' }
}

// Saved/recently-read list responses use snake_case `next_cursor`; normalize
// to the frontend's camelCase convention at the API boundary.
function normalizeListPage(raw: { items: WireArticle[]; next_cursor: string | null }): WireArticleListPage {
  return { items: raw.items.map(normalizeDraft), nextCursor: raw.next_cursor }
}

export const httpArticleService: ArticleService = {
  // -- Canvas lookups --

  async search(query: string, filters?: ArticleSearchFilters): Promise<WireArticle[]> {
    const { data } = await articlesApiClient.post<{ articles: WireArticle[] }>(
      '/search-articles',
      { query, pageSize: 20, maxResults: 100, ...filters },
    )
    return data.articles
  },

  // -- Admin CRUD --

  async listAdmin(params?: ListDraftsParams): Promise<WireArticle[]> {
    const { data } = await publishingApiClient.get<WireArticle[]>('/drafts', { params })
    return data.map(normalizeDraft)
  },

  async getAdmin(id: string): Promise<WireArticleFull> {
    const { data } = await publishingApiClient.get<WireArticleFull>(`/drafts/${id}`)
    return normalizeDraft(data) as WireArticleFull
  },

  async uploadDraft(file: File, metadata: DraftUploadMetadata): Promise<WireArticleFull> {
    const form = new FormData()
    form.append('file', file)
    form.append('classification', metadata.classification)
    if (metadata.disseminationControls?.length) {
      form.append('disseminationControls', JSON.stringify(metadata.disseminationControls))
    }
    if (metadata.title)               form.append('title', metadata.title)
    if (metadata.qcAssignedTo)        form.append('qcAssignedTo',        metadata.qcAssignedTo)
    if (metadata.layoutAssignedTo)    form.append('layoutAssignedTo',    metadata.layoutAssignedTo)
    if (metadata.editorialAssignedTo) form.append('editorialAssignedTo', metadata.editorialAssignedTo)
    if (metadata.postPubAssignedTo)   form.append('postPubAssignedTo',   metadata.postPubAssignedTo)
    if (metadata.topics?.length)  form.append('topics',  JSON.stringify(metadata.topics))
    if (metadata.regions?.length) form.append('regions', JSON.stringify(metadata.regions))
    if (metadata.sensitive)       form.append('sensitive', 'true')
    const { data } = await publishingApiClient.post<WireArticleFull>('/drafts', form, {
      headers: { 'Content-Type': undefined },
    })
    return normalizeDraft(data) as WireArticleFull
  },

  async deleteAdmin(id: string): Promise<void> {
    await publishingApiClient.delete(`/drafts/${id}`)
  },

  // -- Reader --

  async getPublished(id: string, navMethod?: NavMethod, pageId?: string): Promise<WireArticleFull> {
    const params: Record<string, string> = {}
    if (navMethod) params.nav_method = navMethod
    if (pageId) params.page_id = pageId
    const { data } = await articlesApiClient.get<WireArticleFull>(`/articles/${id}`, {
      params: Object.keys(params).length ? params : undefined,
    })
    return normalizeDraft(data) as WireArticleFull
  },

  async getByIds(ids: string[]): Promise<WireArticle[]> {
    const { data } = await articlesApiClient.get<WireArticle[]>('/articles', {
      params: { article_ids: ids.join(',') },
    })
    return data.map(normalizeDraft)
  },

  async listRecommendedArticles(): Promise<WireArticle[]> {
    const { data } = await articlesApiClient.get<WireArticle[]>('/recommended')
    return data.map(normalizeDraft)
  },

  // -- Saved & recently read --

  async saveUserArticle(id: string): Promise<SaveArticleResult> {
    const { data } = await articlesApiClient.post<SaveArticleResult>(`/articles/${id}/save`)
    return data
  },

  async unsaveUserArticle(id: string): Promise<SaveArticleResult> {
    const { data } = await articlesApiClient.delete<SaveArticleResult>(`/articles/${id}/save`)
    return data
  },

  async listSavedArticles(params?: ListSavedArticlesParams): Promise<WireArticleListPage> {
    const { data } = await articlesApiClient.get<{ items: WireArticle[]; next_cursor: string | null }>(
      '/saved-articles',
      { params: { limit: params?.limit, cursor: params?.cursor } },
    )
    return normalizeListPage(data)
  },

  async listRecentlyRead(params?: ListRecentlyReadParams): Promise<WireArticleListPage> {
    const { data } = await articlesApiClient.get<{ items: WireArticle[]; next_cursor: string | null }>(
      '/recently-read',
      { params: { limit: params?.limit } },
    )
    return normalizeListPage(data)
  },

  // -- Review --

  async getReview(id: string): Promise<WireArticleFull> {
    const { data } = await publishingApiClient.get<WireArticleFull>(`/drafts/${id}`)
    return normalizeDraft(data) as WireArticleFull
  },

  async saveReview(article: WireArticleFull): Promise<void> {
    await publishingApiClient.put(`/drafts/${article.id}`, article)
  },

  async patchWorkflowStage(
    id: string,
    stage: PatchWorkflowStage,
    details?: { reason?: string; notes?: string },
  ): Promise<void> {
    await publishingApiClient.patch(`/drafts/${id}`, { stage, ...details })
  },

  async getChangelog(id: string) {
    const { data } = await publishingApiClient.get(`/drafts/${id}/changelog`)
    return data
  },

  async promote(id: string): Promise<void> {
    await publishingApiClient.post(`/drafts/${id}/promote`)
  },
}
